import { ReactNode, useState } from "react";
import {
  Box,
  Column,
  ConfirmationDialog,
  ErrorIcon,
  InformationIcon,
  MarginProps,
  Paragraph,
  ProgressBar,
  Row,
  Text,
  WarningIcon,
} from "@hightouch/htui";
import { useUser } from "src/contexts/user-context";
import { Link } from "src/router";
import { getPredictorUiStatus } from "src/components/traits/predictive-utils";

interface BannerProps {
  variant: "warning" | "info" | "error";
  icon: React.ElementType;
  title: ReactNode;
  description: ReactNode;
}

const Banner = (props: BannerProps & MarginProps) => {
  const { variant, icon, title, description, ...marginProps } = props;

  const color =
    variant === "warning" ? "warning" : variant === "error" ? "danger" : "teal";
  return (
    <Row
      {...marginProps}
      p={4}
      gap={4}
      borderRadius="md"
      borderWidth="hairline"
      borderStyle="solid"
      borderColor={`${color}.border`}
      bgColor={`${color}.background`}
    >
      <Box
        flex="0 0 20px"
        as={icon}
        boxSize={5}
        fontSize="20px"
        alignSelf="flex-start"
        color={`${color}.base`}
      />
      <Column gap={2} flex={1} minWidth={0}>
        <Text fontWeight="semibold">{title}</Text>
        {typeof description === "string" ? (
          <Text>{description}</Text>
        ) : (
          description
        )}
      </Column>
    </Row>
  );
};

export const PredictorRetrainingWarning = () => {
  return (
    <Banner
      variant="warning"
      icon={WarningIcon}
      title="Saving changes will retrigger model training"
      description="New predictions won’t be available until the model is retrained and
          scoring completes."
    />
  );
};

export type PitRiskColumn = {
  columnName: string;
  risk: string;
  reason: string;
};

const PIT_BANNER_COPY = {
  features: {
    leakyTitle: "Some user properties may leak future information",
    reviewTitle: "Review these user properties before relying on predictions",
    leakyDescription:
      "These columns don't appear to be point-in-time correct: their current values would be joined onto historical training data, leaking information about the outcome. We strongly recommend removing them (or capturing the behavior with a key event instead) before saving.",
    reviewDescription:
      "These columns may not be point-in-time correct. Double-check that their values don't change based on recent user behavior.",
  },
  "eligibility-filter": {
    leakyTitle: "Your eligibility filter may leak future information",
    reviewTitle: "Review your eligibility filter before relying on predictions",
    leakyDescription:
      "These filter properties are evaluated at their current values when choosing which users enter training, which can leak information about the outcome. We strongly recommend rewriting these conditions as event filters — events are evaluated point-in-time at each training date.",
    reviewDescription:
      "These filter properties may not be point-in-time correct. Double-check that their values don't change based on recent user behavior — if they do, rewrite the condition as an event filter, which is evaluated point-in-time.",
  },
} as const;

export type PitLeakageSubject = keyof typeof PIT_BANNER_COPY;

/**
 * Surfaces the point-in-time leakage check results for user property columns.
 * Always warning-styled (the classification comes from an LLM reading column
 * names, so it's advisory) and never blocks the save — the user can always
 * save anyway. `leaky_not_pit` columns get stronger copy than `review` ones.
 */
export const PitLeakageWarningBanner = ({
  columns,
  subject = "features",
}: {
  columns: PitRiskColumn[];
  /** What was classified — adjusts the banner copy. */
  subject?: PitLeakageSubject;
}) => {
  const leaky = columns.filter((column) => column.risk === "leaky_not_pit");
  const review = columns.filter((column) => column.risk === "review");

  if (leaky.length === 0 && review.length === 0) {
    return null;
  }

  const hasLeaky = leaky.length > 0;
  const copy = PIT_BANNER_COPY[subject];

  return (
    <Banner
      variant="warning"
      icon={WarningIcon}
      title={hasLeaky ? copy.leakyTitle : copy.reviewTitle}
      description={
        <Column gap={2}>
          <Text>
            {hasLeaky ? copy.leakyDescription : copy.reviewDescription}
          </Text>
          <Column as="ul" pl={5} m={0} gap={1}>
            {[...leaky, ...review].map((column) => (
              <Box as="li" key={column.columnName}>
                <Text fontWeight="semibold">{column.columnName}</Text>
                {column.reason ? <Text>: {column.reason}</Text> : null}
              </Box>
            ))}
          </Column>
        </Column>
      }
    />
  );
};

interface PredictorIsTrainingInfoBoxProps extends MarginProps {
  predictor?: {
    status?: string | null;
    error_message?: string | null;
  };
}

export const PredictorIsTrainingInfoBox = ({
  predictor,
  ...marginProps
}: PredictorIsTrainingInfoBoxProps) => {
  const status = getPredictorUiStatus(predictor);

  if (status.kind !== "in_progress") {
    return null;
  }

  return (
    <Banner
      {...marginProps}
      variant="info"
      icon={InformationIcon}
      title="Training in progress"
      description={
        <Column gap={3} width="100%">
          <Text>{status.tooltip}</Text>
          <Column gap={2} width="100%">
            <Row align="center" justify="space-between" gap={2}>
              <Text size="sm" fontWeight="medium">
                Step {status.stage} of {status.totalStages}
              </Text>
              <Text size="sm" color="text.secondary">
                {status.label}
              </Text>
            </Row>
            <ProgressBar
              aria-label="Predictor training progress"
              value={status.progressPercent}
            />
            <Row width="100%" gap={2}>
              {status.checkpoints.map((checkpoint, index) => {
                const checkpointNumber = index + 1;
                const isComplete = checkpointNumber <= status.stage;
                const isCurrent = checkpointNumber === status.stage;
                const textAlign =
                  index === 0
                    ? "left"
                    : index === status.checkpoints.length - 1
                      ? "right"
                      : "center";

                return (
                  <Box key={checkpoint} flex={1} textAlign={textAlign}>
                    <Text
                      size="sm"
                      fontWeight={isCurrent ? "medium" : "normal"}
                      color={isComplete ? "text.primary" : "text.secondary"}
                    >
                      {checkpoint}
                    </Text>
                  </Box>
                );
              })}
            </Row>
          </Column>
          <Text size="sm" color="text.secondary">
            You’ll see scores appear as soon as inference is complete.
          </Text>
        </Column>
      }
    />
  );
};

interface PredictorFailedErrorBoxProps extends MarginProps {
  additionalDetails?: string | null;
}

export const PredictorFailedErrorBox = (
  props: PredictorFailedErrorBoxProps,
) => {
  const message =
    props.additionalDetails ??
    "Check that your outcome event, eligible users, and predictive inputs are valid.";
  const isInternalError = message.toLowerCase().includes("internal error");
  const description = isInternalError
    ? message
    : `${message} You can edit the configuration and retry.`;
  return (
    <Banner
      {...props}
      variant="error"
      icon={ErrorIcon}
      title="Predictor failed"
      description={description}
    />
  );
};

export const PredictorUpdateFailedWarning = (
  props: PredictorFailedErrorBoxProps,
) => {
  const { additionalDetails, ...marginProps } = props;
  return (
    <Banner
      {...marginProps}
      variant="warning"
      icon={WarningIcon}
      title="Model update failed"
      description={`${additionalDetails ?? "The latest model update failed."} Scores still use the last successful model.`}
    />
  );
};

export const DemoWorkspaceRealPredictorWarning = () => {
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(true);

  if (!user?.can_impersonate || user.is_impersonating) {
    return null;
  }

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      title="You're creating a real predictor in an internal workspace"
      variant="warning"
      confirmButtonText="I understand, continue"
      onClose={() => setIsOpen(false)}
      onConfirm={() => setIsOpen(false)}
    >
      <Paragraph>
        If this is for a demo, use the demo predictor tool at{" "}
        <Link href="/traits/admin">/traits/admin</Link> instead.
      </Paragraph>
    </ConfirmationDialog>
  );
};
